import React from 'react';
import { Sparkles, Wrench, LayoutGrid, KeyRound, TrainFront, SquareParking, ChefHat, Megaphone, Store, Landmark } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { useContent } from '../content/ContentContext';
import { Reveal, Kicker } from '../components/Reveal';
import { SEO } from '../components/SEO';
import { IMG, Banner } from './Home';

const featureIcons = [Sparkles, Wrench, LayoutGrid, KeyRound];
const rentIcons = [ChefHat, Megaphone, Store, Landmark];

export default function Location() {
  const { t } = useLanguage();
  const { img } = useContent();
  const floorImgs = [img('loc-floor0', IMG.hero), img('loc-floor1', '/images/spazio-vuoto-wide.jpg')];

  return (
    <div data-testid="location-page">
      <SEO title={t.meta.locationTitle} description={t.meta.locationDesc} keywords={t.meta.locationKeys} />

      <section className="relative pt-40 pb-24 md:pb-32 bg-emerald-deep grain overflow-hidden" data-testid="location-hero">
        <div className="absolute inset-0 opacity-20">
          <img src={img('loc-hero', IMG.locationEvento)} alt="" className="h-full w-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-emerald-deep/60" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <Reveal>
            <Kicker light>{t.location.kicker}</Kicker>
            <h1 className="mt-6 font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory tracking-tight max-w-3xl">{t.location.title}</h1>
            <p className="mt-6 max-w-2xl text-ivory/70 text-base md:text-lg leading-relaxed">{t.location.intro}</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="location-floors">
        <Reveal>
          <Kicker>{t.location.floorsKicker}</Kicker>
        </Reveal>
        <div className="mt-12 space-y-20">
          {t.location.floors.map((f, i) => (
            <div key={f.title} className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
              <div className={`md:col-span-7 ${i % 2 === 1 ? 'md:order-2' : ''}`}>
                <Reveal>
                  <div className="img-frame rounded-sm shadow-[0_8px_40px_rgba(37,79,87,0.15)]">
                    <img src={floorImgs[i]} alt={f.title} className="w-full aspect-[16/10] object-cover" loading="lazy" />
                  </div>
                </Reveal>
              </div>
              <div className={`md:col-span-5 ${i % 2 === 1 ? 'md:order-1' : ''}`}>
                <Reveal delay={0.15}>
                  <span className="font-serif italic text-terracotta/70 text-6xl">{i === 0 ? '0' : '-1'}</span>
                  <h2 className="mt-4 font-serif text-3xl md:text-4xl text-emerald tracking-tight">{f.title}</h2>
                  <p className="mt-5 text-emerald-ink/70 text-base md:text-lg leading-relaxed">{f.text}</p>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-ivory-soft border-y border-emerald/10" data-testid="location-features">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <Reveal>
            <Kicker>{t.location.featuresKicker}</Kicker>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.location.features.map((f, i) => {
              const Icon = featureIcons[i];
              return (
                <Reveal key={f.title} delay={i * 0.08}>
                  <article
                    data-testid={`location-feature-${i}`}
                    className="h-full bg-white border border-emerald/10 rounded-sm p-8 transition-all duration-500 hover:shadow-[0_12px_50px_rgba(37,79,87,0.14)] hover:-translate-y-1"
                  >
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-ivory text-terracotta-dark">
                      <Icon size={22} strokeWidth={1.5} />
                    </span>
                    <h3 className="mt-6 font-serif text-2xl text-emerald">{f.title}</h3>
                    <p className="mt-3 text-sm text-emerald-ink/60 leading-relaxed">{f.desc}</p>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-emerald grain relative" data-testid="location-rent">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
          <Reveal>
            <Kicker light>{t.location.rentKicker}</Kicker>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-ivory tracking-tight max-w-3xl">{t.location.rentTitle}</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.location.rentCases.map((c, i) => {
              const Icon = rentIcons[i];
              return (
                <Reveal key={c.title} delay={i * 0.08}>
                  <article
                    data-testid={`rent-case-${i}`}
                    className="h-full border border-ivory/15 rounded-sm p-8 bg-emerald-deep/40 transition-all duration-500 hover:border-terracotta/50 hover:-translate-y-1"
                  >
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-ivory/10 text-terracotta">
                      <Icon size={22} strokeWidth={1.5} />
                    </span>
                    <h3 className="mt-6 font-serif text-2xl text-ivory">{c.title}</h3>
                    <p className="mt-3 text-sm text-ivory/60 leading-relaxed">{c.desc}</p>
                  </article>
                </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
              <div className="border-l-2 border-terracotta pl-6" data-testid="rent-note">
                <p className="text-ivory/80 text-base md:text-lg leading-relaxed">{t.location.rentNote}</p>
              </div>
              <div className="img-frame rounded-sm border border-ivory/15 overflow-hidden" data-testid="rent-photo">
                <img src={img('loc-rent', '/images/spazio-vuoto.jpg')} alt={t.location.rentKicker} className="w-full aspect-[4/3] object-cover" loading="lazy" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>


      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="location-reach">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
          <Reveal>
            <Kicker>{t.location.reachKicker}</Kicker>
            <h2 className="mt-4 font-serif text-4xl text-emerald tracking-tight">{t.location.reachTitle}</h2>
            <p className="mt-5 text-emerald-ink/70 text-base md:text-lg leading-relaxed">{t.location.address}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="space-y-5">
              <div className="flex items-start gap-4 bg-white border border-emerald/10 rounded-sm p-6">
                <TrainFront size={22} className="text-terracotta-dark shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="text-sm md:text-base text-emerald-ink/70 leading-relaxed" data-testid="reach-transit">{t.location.reachText}</p>
              </div>
              <div className="flex items-start gap-4 bg-white border border-emerald/10 rounded-sm p-6">
                <SquareParking size={22} className="text-terracotta-dark shrink-0 mt-0.5" strokeWidth={1.5} />
                <p className="text-sm md:text-base text-emerald-ink/70 leading-relaxed">Via Gustavo Modena 10, Milano</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Banner title={t.location.bannerTitle} text={t.location.bannerText} cta={t.location.bannerCta} />
    </div>
  );
}
